/**
 * useFeeManagementHandlers
 * FeeManagement 핸들러 모음 (상태 변경, 저장, 히스토리 등)
 */
import { useCallback } from 'react';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { SaveSupplierCommissionResponse } from '../api/supplierCommissionApi';
import { supplierCommissionApi } from '../api/supplierCommissionApi';
import {
    applyDefaultValuesToAll,
    filterCommissionData,
} from '../utils/feeManagementHelpers';
import { CommissionHistoryItem, PeriodType, SupplierCommissionRow } from '../types/feeManagement.types';

interface UseFeeManagementHandlersParams {
    commissionData: SupplierCommissionRow[];
    allCommissionData: SupplierCommissionRow[];
    partnerId: string;
    statusFilter: string;
    defaultDailyAmount: string;
    defaultWeeklyAmount: string;
    setCommissionData: React.Dispatch<React.SetStateAction<SupplierCommissionRow[]>>;
    setStatusFilter: React.Dispatch<React.SetStateAction<string>>;
    setHistoryData: React.Dispatch<React.SetStateAction<CommissionHistoryItem[]>>;
    setSelectedSupplier: React.Dispatch<React.SetStateAction<string>>;
    setShowHistoryModal: React.Dispatch<React.SetStateAction<boolean>>;
    setFeeApplyDateInfo: React.Dispatch<React.SetStateAction<{ applyStartDate: string; isImmediateApply: boolean } | null>>;
    setShowFeeApplyModal: React.Dispatch<React.SetStateAction<boolean>>;
    resetPagination: () => void;
    saveCommission: (row: SupplierCommissionRow) => Promise<SaveSupplierCommissionResponse | null>;
    fetchSuppliers: () => Promise<void>;
}

export const useFeeManagementHandlers = ({
                                             commissionData,
                                             allCommissionData,
                                             partnerId,
                                             statusFilter,
                                             defaultDailyAmount,
                                             defaultWeeklyAmount,
                                             setCommissionData,
                                             setStatusFilter,
                                             setHistoryData,
                                             setSelectedSupplier,
                                             setShowHistoryModal,
                                             setFeeApplyDateInfo,
                                             setShowFeeApplyModal,
                                             resetPagination,
                                             saveCommission,
                                             fetchSuppliers,
                                         }: UseFeeManagementHandlersParams) => {
    const { showToast } = useToast();

    const handleSearch = useCallback(() => {
        const filtered = filterCommissionData(allCommissionData, partnerId, statusFilter);
        setCommissionData(filtered);
        resetPagination();
        if (filtered.length === 0) {
            showToast('조회된 데이터가 없습니다.', { type: ToastType.INFO });
        } else {
            showToast(`${filtered.length}개의 협력사가 조회되었습니다.`, { type: ToastType.SUCCESS });
        }
    }, [allCommissionData, partnerId, statusFilter, resetPagination, showToast, setCommissionData]);

    const handleQuickFilter = useCallback((status: string) => {
        setStatusFilter(status);
        const filtered = filterCommissionData(allCommissionData, partnerId, status);
        setCommissionData(filtered);
        resetPagination();
    }, [allCommissionData, partnerId, resetPagination, setCommissionData, setStatusFilter]);

    const handleSetDefaultValues = useCallback(() => {
        setCommissionData(prev => applyDefaultValuesToAll(prev, defaultDailyAmount, defaultWeeklyAmount));
        showToast('모든 협력사에 초기값이 설정되었습니다.', { type: ToastType.SUCCESS });
    }, [defaultDailyAmount, defaultWeeklyAmount, showToast, setCommissionData]);

    const handleSetDefaultPayback = useCallback(() => {
        const DEFAULT_PAYBACK_AMOUNT = '30';
        setCommissionData(prev => prev.map(row => {
            if (row.status !== 'OPERATING') return row;
            return {
                ...row,
                newDailyCommissionType: row.dailyCommissionType || '',
                newDailyAmount: row.dailyAmount || '',
                newWeeklyCommissionType: row.weeklyCommissionType || '',
                newWeeklyAmount: row.weeklyAmount || '',
                newStartDate: row.startDate || '',
                newWeeklyPaybackUseYn: 'Y',
                newWeeklyPaybackAmount: DEFAULT_PAYBACK_AMOUNT,
                isAddingNew: true,
                isEdited: true
            };
        }));
        showToast('모든 협력사에 페이백 초기값(30원, 사용)이 설정되었습니다.', { type: ToastType.SUCCESS });
    }, [showToast, setCommissionData]);

    const handleToggleFeeChange = useCallback((supplierId: string) => {
        setCommissionData(prev => prev.map(row => {
            if (row.supplierId !== supplierId) return row;

            if (row.isAddingNew) {
                return {
                    ...row,
                    isAddingNew: false,
                    newDailyCommissionType: undefined,
                    newDailyAmount: undefined,
                    newWeeklyCommissionType: undefined,
                    newWeeklyAmount: undefined,
                    newWeeklyPaybackUseYn: undefined,
                    newWeeklyPaybackAmount: undefined,
                    newStartDate: undefined,
                    isEdited: false
                };
            }

            return {
                ...row,
                isAddingNew: true,
                newDailyCommissionType: row.dailyCommissionType || '',
                newDailyAmount: row.dailyAmount || '',
                newWeeklyCommissionType: row.weeklyCommissionType || '',
                newWeeklyAmount: row.weeklyAmount || '',
                newWeeklyPaybackUseYn: row.weeklyPaybackUseYn || 'N',
                newWeeklyPaybackAmount: row.weeklyPaybackAmount || '',
                newStartDate: row.startDate || '',
                isEdited: true
            };
        }));
    }, [setCommissionData]);

    const handleNewCommissionTypeChange = useCallback((supplierId: string, period: PeriodType, value: string) => {
        setCommissionData(prev => prev.map(row => {
            if (row.supplierId !== supplierId) return row;
            if (period === 'daily') {
                return { ...row, newDailyCommissionType: value, newDailyAmount: '', isEdited: true };
            }
            return { ...row, newWeeklyCommissionType: value, newWeeklyAmount: '', isEdited: true };
        }));
    }, [setCommissionData]);

    const handleNewAmountChange = useCallback((supplierId: string, period: PeriodType, value: string) => {
        setCommissionData(prev => prev.map(row => {
            if (row.supplierId !== supplierId) return row;
            return {
                ...row,
                [period === 'daily' ? 'newDailyAmount' : 'newWeeklyAmount']: value,
                isEdited: true
            };
        }));
    }, [setCommissionData]);

    const handleNewStartDateChange = useCallback((supplierId: string, value: string) => {
        setCommissionData(prev => prev.map(row =>
            row.supplierId === supplierId ? { ...row, newStartDate: value, isEdited: true } : row
        ));
    }, [setCommissionData]);

    const handleNewPaybackUseYnChange = useCallback((supplierId: string, value: string) => {
        setCommissionData(prev => prev.map(row => {
            if (row.supplierId !== supplierId) return row;
            return {
                ...row,
                newWeeklyPaybackUseYn: value,
                newWeeklyPaybackAmount: value === 'N' ? '' : row.newWeeklyPaybackAmount,
                isEdited: true
            };
        }));
    }, [setCommissionData]);

    const handleNewPaybackAmountChange = useCallback((supplierId: string, value: string) => {
        setCommissionData(prev => prev.map(row =>
            row.supplierId === supplierId ? { ...row, newWeeklyPaybackAmount: value, isEdited: true } : row
        ));
    }, [setCommissionData]);

    const handleSave = useCallback(async (supplierId: string) => {
        const row = commissionData.find(r => r.supplierId === supplierId);
        if (!row || !row.isAddingNew) return;

        if (!row.newStartDate) {
            showToast('적용일을 입력해주세요.', { type: ToastType.WARNING });
            return;
        }

        const hasNewDailyCommission = row.newDailyCommissionType && row.newDailyAmount;
        const hasNewWeeklyCommission = row.newWeeklyCommissionType && row.newWeeklyAmount;

        if (!hasNewDailyCommission && !hasNewWeeklyCommission) {
            showToast('일정산 또는 주정산 수수료를 입력해주세요.', { type: ToastType.WARNING });
            return;
        }

        const saveData = {
            ...row,
            dailyCommissionType: row.newDailyCommissionType || row.dailyCommissionType,
            dailyAmount: row.newDailyAmount || row.dailyAmount,
            weeklyCommissionType: row.newWeeklyCommissionType || row.weeklyCommissionType,
            weeklyAmount: row.newWeeklyAmount || row.weeklyAmount,
            weeklyPaybackUseYn: row.newWeeklyPaybackUseYn ?? row.weeklyPaybackUseYn ?? 'N',
            weeklyPaybackAmount: row.newWeeklyPaybackAmount || row.weeklyPaybackAmount || '',
            startDate: row.newStartDate
        };

        const response = await saveCommission(saveData);
        if (response) {
            showToast('수수료가 저장되었습니다.', { type: ToastType.SUCCESS });
            await fetchSuppliers();
            setFeeApplyDateInfo({
                applyStartDate: response.applyStartDate,
                isImmediateApply: response.isImmediateApply
            });
            setShowFeeApplyModal(true);
        }
    }, [commissionData, showToast, saveCommission, fetchSuppliers, setFeeApplyDateInfo, setShowFeeApplyModal]);

    const handleSaveAll = useCallback(async () => {
        const editedRows = commissionData.filter(row => row.isAddingNew && row.isEdited);

        if (editedRows.length === 0) {
            showToast('저장할 변경사항이 없습니다.', { type: ToastType.INFO });
            return;
        }

        const invalidRows = editedRows.filter(row => {
            const hasNewDailyCommission = row.newDailyCommissionType && row.newDailyAmount;
            const hasNewWeeklyCommission = row.newWeeklyCommissionType && row.newWeeklyAmount;
            return !row.newStartDate || (!hasNewDailyCommission && !hasNewWeeklyCommission);
        });

        if (invalidRows.length > 0) {
            showToast(`${invalidRows.length}개 협력사의 적용일 또는 수수료 정보가 누락되었습니다.`, { type: ToastType.WARNING });
            return;
        }

        let successCount = 0;
        let failCount = 0;
        let lastResponse: SaveSupplierCommissionResponse | null = null;

        for (const row of editedRows) {
            const saveData = {
                ...row,
                dailyCommissionType: row.newDailyCommissionType || row.dailyCommissionType,
                dailyAmount: row.newDailyAmount || row.dailyAmount,
                weeklyCommissionType: row.newWeeklyCommissionType || row.weeklyCommissionType,
                weeklyAmount: row.newWeeklyAmount || row.weeklyAmount,
                weeklyPaybackUseYn: row.newWeeklyPaybackUseYn ?? row.weeklyPaybackUseYn ?? 'N',
                weeklyPaybackAmount: row.newWeeklyPaybackAmount || row.weeklyPaybackAmount || '',
                startDate: row.newStartDate || ''
            };

            const response = await saveCommission(saveData);
            if (response) {
                successCount++;
                lastResponse = response;
            } else {
                failCount++;
            }
        }

        if (failCount === 0) {
            showToast(`${successCount}개 협력사 수수료가 저장되었습니다.`, { type: ToastType.SUCCESS });
        } else {
            showToast(`${successCount}개 성공, ${failCount}개 실패`, { type: ToastType.WARNING });
        }

        await fetchSuppliers();

        if (lastResponse) {
            setFeeApplyDateInfo({
                applyStartDate: lastResponse.applyStartDate,
                isImmediateApply: lastResponse.isImmediateApply
            });
            setShowFeeApplyModal(true);
        }
    }, [commissionData, showToast, saveCommission, fetchSuppliers, setFeeApplyDateInfo, setShowFeeApplyModal]);

    const handleShowHistory = useCallback(async (supplierId: string, supplierName: string) => {
        try {
            const history = await supplierCommissionApi.getCommissionHistory(supplierId);
            setHistoryData(history);
        } catch {
            showToast('히스토리를 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setHistoryData([]);
        }
        setSelectedSupplier(supplierName);
        setShowHistoryModal(true);
    }, [showToast, setHistoryData, setSelectedSupplier, setShowHistoryModal]);

    return {
        handleSearch,
        handleQuickFilter,
        handleSetDefaultValues,
        handleSetDefaultPayback,
        handleToggleFeeChange,
        handleNewCommissionTypeChange,
        handleNewAmountChange,
        handleNewStartDateChange,
        handleNewPaybackUseYnChange,
        handleNewPaybackAmountChange,
        handleSave,
        handleSaveAll,
        handleShowHistory,
    };
};
